import type { JSX } from 'react'
import { api, call } from '@/api/client'
import { useMutation, useQuery } from '@/api/query'
import { ApprovalsForm } from '@/settings/approvals/form'
import type { GatingBody } from '@/settings/approvals/form'
import { ErrorState, Loading } from '@/ui/states'
import { NoSiteYet, SettingsStepShell } from './SettingsStepShell'
import { useWizard } from './context'
import { useSettleStep } from './settingsStep'

/**
 * Step 11, the last — what may change without asking, through
 * `/settings/approvals`' form. S-D2's last step keeps the meter and drops the
 * rail (ONB-33): the meter is at 100 % here and that is the point of drawing
 * it, where a rail inviting a jump back competes with finishing.
 */
export function ApprovalsStep(): JSX.Element {
  const state = useWizard()
  const site = state.site ?? null
  const gating = useQuery(
    (signal) => call(() => api.GET('/api/v1/seo/approvals/sites/gating', { signal })),
    [],
  )
  const row = (gating.state.data?.sites ?? []).find((entry) => entry.site_id === site?.id) ?? null
  const save = useMutation((body: { site_id: string; gating: GatingBody }) =>
    call(() =>
      api.PUT('/api/v1/seo/approvals/sites/{site_id}/gating', {
        params: { path: { site_id: body.site_id } },
        body: body.gating,
      }),
    ),
  )
  const { settle, failure } = useSettleStep('approvals')

  return (
    <SettingsStepShell step="approvals" failure={failure} rail={false} offerSkip={false}>
      {site === null ? <NoSiteYet /> : null}
      {site !== null && gating.state.status === 'loading' ? (
        <Loading label="Loading" lines={2} />
      ) : null}
      {gating.state.status === 'error' ? (
        <ErrorState failure={gating.state.failure} onRetry={gating.refetch} />
      ) : null}
      {site !== null && row !== null ? (
        <ApprovalsForm
          site={row}
          submitLabel="Finish setup"
          onSubmit={async (body) => {
            const result = await save.run({ site_id: row.site_id, gating: body })
            if (result.ok) await settle()
            return result
          }}
        />
      ) : null}
    </SettingsStepShell>
  )
}
